/* Persistent top chrome for CustomerAppShell — ← · Org · Cart · Settings · Profile.
 * HUX-01 root `/browse`: no Back/title/Org; cart hide-empty; Settings/Profile → More. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CUSTOMER_SHOP_CART_CHANGED_EVENT,
} from '../features/shop/cart/cartStorage.js';
import {
  CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT,
} from '../shared/session/lastKnownSalesPoint.js';
import { persistCheckoutLeaveOffQrFromSearch } from '../features/checkout/checkoutInFlowStepStorage.js';
import {
  PLATFORM_SHOPS_PATH,
  resolveLastKnownSalesPointHref,
  resolveListableLastKnownSalesPointHref,
  resolvePurchasesNavHref,
} from '../features/shop/resolveShopHref.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { CustomerBackLink } from '../shared/ui/CustomerBackLink.js';
import {
  HEADER_OPEN_CART_STATE,
  buildHeaderOpenCartNavigateArgs,
} from './buildHeaderOpenCartNavigateArgs.js';
import { CustomerOrganizationControl } from './CustomerOrganizationControl.js';
import { CustomerLocationDisclosureSheet } from './CustomerLocationDisclosureSheet.js';
import { CustomerProfileButton } from './CustomerProfileButton.js';
import { CustomerProfileSheet } from './CustomerProfileSheet.js';
import { CustomerSettingsButton } from './CustomerSettingsButton.js';
import { CustomerSettingsSheet } from './CustomerSettingsSheet.js';
import { CustomerShellCartControl } from './CustomerShellCartControl.js';
import {
  useDiscoveryShellLocation,
  useDiscoveryShellLocationDisclosure,
} from './DiscoveryShellLocationContext.js';
import { dismissOverlayThen } from './dismissOverlayThen.js';
import { HubEmptyCartSheet } from './HubEmptyCartSheet.js';
import { MultiCartSwitcherSheet } from './MultiCartSwitcherSheet.js';
import {
  HEADER_HUB_EMPTY_CART_STATE,
  hubEmptyCartStripWouldChangeState,
  resolveHubEmptyCartConsumeAction,
  stripHubEmptyCartIntent,
} from './hubEmptyCartIntent.js';
import { useMoreShellOptional } from './MoreShellContext.js';
import {
  isRootBrowseHomePath,
  resolveShellHeaderTitle,
} from './resolveShellHeaderTitle.js';
import { useShellPlaceTitle } from './ShellPlaceTitleContext.js';
import {
  readLastKnownShopCartQuantity,
  resolveLastKnownCartReaderTenantCode,
  useLastKnownShopCartQuantity,
} from './useLastKnownShopCartQuantity.js';
import {
  useHasAnyNonemptyPersistedCart,
  useMultiCartRegistryRows,
} from './useMultiCartRegistry.js';
import { hasAnyNonemptyPersistedCart } from '../features/shop/cart/cartRegistry.js';
import type { ShellDepthBackTarget } from './useShellDepthBack.js';
import {
  CUSTOMER_ACCOUNT_HEADER_CENTER_SLOT_CLASS,
  CUSTOMER_ACCOUNT_HEADER_END_ICONS_CLASS,
  CUSTOMER_ACCOUNT_HEADER_END_SLOT_CLASS,
  CUSTOMER_ACCOUNT_HEADER_INNER_CLASS,
  CUSTOMER_ACCOUNT_HEADER_START_SLOT_CLASS,
  CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS,
  CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS,
  CUSTOMER_TOP_BAR_TITLE_CLASS,
} from './customerTopBarStyles.js';

export { AccountThemeToggle } from './AccountThemeToggle.js';

const SHELL_BACK_TEST_ID = 'customer-shell-hierarchical-back';

function ShellDepthBackSlot({
  depthBack,
}: {
  readonly depthBack: ShellDepthBackTarget;
}): JSX.Element {
  if (depthBack.mode === 'step') {
    return (
      <CustomerBackLink
        onBack={depthBack.onBack}
        disabled={depthBack.disabled}
        iconOnly
        testId={SHELL_BACK_TEST_ID}
      />
    );
  }
  if (depthBack.mode === 'inert') {
    return (
      <CustomerBackLink
        onBack={() => undefined}
        disabled
        iconOnly
        testId={SHELL_BACK_TEST_ID}
      />
    );
  }
  return <CustomerBackLink to={depthBack.to} iconOnly testId={SHELL_BACK_TEST_ID} />;
}

interface CustomerShellHeaderProps {
  /** Depth ← from useShellDepthBack — always set (link / step / inert on absolute `/`). */
  readonly depthBack: ShellDepthBackTarget;
}

/**
 * Global chrome — ← · Org · Cart · Settings · Profile on most journeys.
 * HUX-01 root `/browse`: no permanent Back / visible title / Org; cart hide-when-empty;
 * Settings/Profile not first-row (MoreDrawer / `/more` / `/account/**` via sheet openers).
 * Language / appearance / guest cookies: Settings gear (sheet stays mounted).
 * Shop CartSheet still uses BottomSheet + `--customer-bottom-chrome` when mounted.
 */
export function CustomerShellHeader({
  depthBack,
}: CustomerShellHeaderProps): JSX.Element {
  const { t } = useTranslation('customer');
  const location = useLocation();
  const navigate = useNavigate();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const overlayBack = useOptionalOverlayBackDismiss();
  const moreShell = useMoreShellOptional();
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isRootBrowseHome = isRootBrowseHomePath(location.pathname);
  const [hubEmptyCartHeld, setHubEmptyCartHeld] = useState(false);
  const [multiCartSwitcherOpen, setMultiCartSwitcherOpen] = useState(false);
  const {
    locationDisclosureOpen,
    closeLocationDisclosure,
  } = useDiscoveryShellLocationDisclosure();
  // Profile sheet stays available on guest-auth too (person icon never hides).
  const profileSheetOpen = profileOpen;

  const cartQty = useLastKnownShopCartQuantity();
  const multiCartRows = useMultiCartRegistryRows();
  const hasAnyPersistedCart = useHasAnyNonemptyPersistedCart();
  const hubEmptyConsumeAction = resolveHubEmptyCartConsumeAction(location.state);
  const hubEmptyIntent = hubEmptyConsumeAction === 'open_and_clear';
  // G6 / G8 / G12 — hub-empty latch: React adjust-state-when-prop-changes pattern
  // (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  // Layout-effect setState is forbidden (react-hooks/set-state-in-effect); ref-during-render
  // is forbidden (react-hooks/refs). This is the chosen ESLint-compliant OR-closure for G2/G6.
  // prev starts false so /browse and / entry with open_and_clear still latches held before strip.
  const [prevHubEmptyIntent, setPrevHubEmptyIntent] = useState(false);
  if (hubEmptyIntent !== prevHubEmptyIntent) {
    setPrevHubEmptyIntent(hubEmptyIntent);
    if (hubEmptyIntent) {
      setHubEmptyCartHeld(true);
    }
  }
  // Never show hub-empty chrome when any persisted cart has items.
  const hubEmptyCartOpen =
    !hasAnyPersistedCart &&
    cartQty === 0 &&
    (hubEmptyCartHeld || hubEmptyIntent);

  const clearHubEmptyLatch = useCallback((): void => {
    setHubEmptyCartHeld(false);
  }, []);

  // G12 — block duplicate replace-clear for open_and_clear | clear_only (cleanup for StrictMode).
  const hubEmptyStripInFlightRef = useRef(false);

  // Strip hub-empty keys after commit — useEffect (not layout) so MemoryRouter accepts
  // replace-clear on /browse and / entry; layout-effect navigate can loop or no-op on mount.
  useEffect(() => {
    const action = resolveHubEmptyCartConsumeAction(location.state);
    if (action === 'noop') {
      hubEmptyStripInFlightRef.current = false;
      return;
    }
    // G12: open_and_clear and clear_only both need replace-clear.
    if (hubEmptyStripInFlightRef.current) {
      return;
    }
    if (!hubEmptyCartStripWouldChangeState(location.state)) {
      return;
    }
    hubEmptyStripInFlightRef.current = true;
    navigate(
      `${location.pathname}${location.search}${location.hash}`,
      { replace: true, state: stripHubEmptyCartIntent(location.state) },
    );
    return () => {
      hubEmptyStripInFlightRef.current = false;
    };
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const toggleProfile = useCallback(() => {
    if (profileOpen) {
      setProfileOpen(false);
      return;
    }
    dismissOverlayThen(overlayBack?.overlayDismiss, () => {
      clearHubEmptyLatch();
      setSettingsOpen(false);
      setProfileOpen(true);
    });
  }, [clearHubEmptyLatch, overlayBack?.overlayDismiss, profileOpen]);

  const openProfileSheet = useCallback(() => {
    dismissOverlayThen(overlayBack?.overlayDismiss, () => {
      clearHubEmptyLatch();
      setSettingsOpen(false);
      setProfileOpen(true);
    });
  }, [clearHubEmptyLatch, overlayBack?.overlayDismiss]);

  const closeProfile = useCallback(() => {
    setProfileOpen(false);
  }, []);

  const toggleSettings = useCallback(() => {
    if (settingsOpen) {
      setSettingsOpen(false);
      return;
    }
    dismissOverlayThen(overlayBack?.overlayDismiss, () => {
      clearHubEmptyLatch();
      setProfileOpen(false);
      setSettingsOpen(true);
    });
  }, [clearHubEmptyLatch, overlayBack?.overlayDismiss, settingsOpen]);

  const openSettingsSheet = useCallback(() => {
    dismissOverlayThen(overlayBack?.overlayDismiss, () => {
      clearHubEmptyLatch();
      setProfileOpen(false);
      setSettingsOpen(true);
    });
  }, [clearHubEmptyLatch, overlayBack?.overlayDismiss]);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  // HUX-01 — MoreDrawer / `/more` secondary opens the same chrome sheets.
  useEffect(() => {
    if (moreShell === null) {
      return undefined;
    }
    moreShell.registerShellSheetOpeners({
      openSettings: openSettingsSheet,
      openProfile: openProfileSheet,
    });
    return () => {
      moreShell.registerShellSheetOpeners(null);
    };
  }, [moreShell, openProfileSheet, openSettingsSheet]);

  const closeHubEmptyCart = useCallback((): void => {
    clearHubEmptyLatch();
  }, [clearHubEmptyLatch]);

  const closeMultiCartSwitcher = useCallback((): void => {
    setMultiCartSwitcherOpen(false);
  }, []);

  const onBrowseShops = useCallback((): void => {
    clearHubEmptyLatch();
    navigate(PLATFORM_SHOPS_PATH);
  }, [clearHubEmptyLatch, navigate]);

  const openHeaderCartDestination = useCallback((): void => {
    // G1: sync before navigate — unmount microtask epoch skip can drop persist.
    persistCheckoutLeaveOffQrFromSearch(location.search);
    setProfileOpen(false);
    setSettingsOpen(false);

    // G2: same Obchody listability gate as Purchases nav (fail-closed when empty).
    const readerKey = resolveLastKnownCartReaderTenantCode(tenantCode);
    let lastHref = resolveListableLastKnownSalesPointHref(readerKey);
    if (lastHref === null && readLastKnownShopCartQuantity(readerKey) > 0) {
      // Browse / discovery can write cart + lastKnown before /shops warms listability cache.
      lastHref = resolveLastKnownSalesPointHref(readerKey);
    }
    const hubHref = resolvePurchasesNavHref(() => null, session, readerKey);
    const args = buildHeaderOpenCartNavigateArgs({
      currentPathname: location.pathname,
      currentSearch: location.search,
      lastKnownSalesPointHref: lastHref,
      hubHref,
    });

    if (lastHref === null) {
      // Hub: persist only via location.state — CookieConsentByTenant remount wipes setState.
      // G10: never openCart / openCartSource on hub navigate.
      navigate(args.to, {
        replace: args.replace,
        state: HEADER_HUB_EMPTY_CART_STATE,
      });
      return;
    }

    clearHubEmptyLatch();
    navigate(args.to, {
      replace: args.replace,
      state: HEADER_OPEN_CART_STATE,
    });
  }, [
    clearHubEmptyLatch,
    location.pathname,
    location.search,
    navigate,
    session,
    tenantCode,
  ]);

  // G4 — dismiss hub-empty sheet when cart gains items or lastKnown SP switches to a carted SP.
  useEffect(() => {
    const dismissHubEmptyWhenCartHasItems = (): void => {
      const liveReaderKey = resolveLastKnownCartReaderTenantCode(tenantCode);
      if (
        readLastKnownShopCartQuantity(liveReaderKey) > 0 ||
        hasAnyNonemptyPersistedCart()
      ) {
        clearHubEmptyLatch();
      }
    };
    if (typeof window === 'undefined') {
      return undefined;
    }
    window.addEventListener(
      CUSTOMER_SHOP_CART_CHANGED_EVENT,
      dismissHubEmptyWhenCartHasItems,
    );
    window.addEventListener(
      CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT,
      dismissHubEmptyWhenCartHasItems,
    );
    return () => {
      window.removeEventListener(
        CUSTOMER_SHOP_CART_CHANGED_EVENT,
        dismissHubEmptyWhenCartHasItems,
      );
      window.removeEventListener(
        CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT,
        dismissHubEmptyWhenCartHasItems,
      );
    };
  }, [clearHubEmptyLatch, tenantCode]);

  const toggleHeaderCart = useCallback(() => {
    if (multiCartSwitcherOpen) {
      setMultiCartSwitcherOpen(false);
      return;
    }
    // G5 — hub empty open + cart has items: one click navigates to shop (not dismiss-only).
    if (hubEmptyCartOpen) {
      const readerKey = resolveLastKnownCartReaderTenantCode(tenantCode);
      if (readLastKnownShopCartQuantity(readerKey) > 0) {
        clearHubEmptyLatch();
        openHeaderCartDestination();
        return;
      }
      clearHubEmptyLatch();
      return;
    }
    if (multiCartRows.length > 1) {
      dismissOverlayThen(overlayBack?.overlayDismiss, () => {
        setProfileOpen(false);
        setSettingsOpen(false);
        setMultiCartSwitcherOpen(true);
      });
      return;
    }
    dismissOverlayThen(overlayBack?.overlayDismiss, openHeaderCartDestination);
  }, [
    clearHubEmptyLatch,
    hubEmptyCartOpen,
    multiCartRows.length,
    multiCartSwitcherOpen,
    openHeaderCartDestination,
    overlayBack?.overlayDismiss,
    tenantCode,
  ]);

  // G9 / Stage 3 §5 A6 — sales-point catalog publishes place via ShellPlaceTitleProvider.
  const placeTitle = useShellPlaceTitle();
  const headerTitle = resolveShellHeaderTitle(location.pathname, tenantCode, t, {
    search: location.search,
    placeTitle,
  });
  const discoveryLocation = useDiscoveryShellLocation();

  return (
    <>
      <header className={CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS} data-testid="customer-shell-header">
        <div className={CUSTOMER_ACCOUNT_HEADER_INNER_CLASS}>
          <div className={CUSTOMER_ACCOUNT_HEADER_START_SLOT_CLASS}>
            {/* HUX-01: root Home — no permanent Back visual slot. */}
            {isRootBrowseHome ? null : <ShellDepthBackSlot depthBack={depthBack} />}
          </div>
          <div className={CUSTOMER_ACCOUNT_HEADER_CENTER_SLOT_CLASS}>
            {headerTitle.length > 0 ? (
              <h1
                id="customer-shell-header-title"
                className={CUSTOMER_TOP_BAR_TITLE_CLASS}
                data-testid="customer-shell-header-title"
              >
                {headerTitle}
              </h1>
            ) : null}
          </div>
          <div className={CUSTOMER_ACCOUNT_HEADER_END_SLOT_CLASS}>
            <div className={CUSTOMER_ACCOUNT_HEADER_END_ICONS_CLASS}>
              {/* HUX-01: Org / Settings / Profile leave root Home first-row → More. */}
              {isRootBrowseHome ? null : <CustomerOrganizationControl />}
              <CustomerShellCartControl
                hubEmptyCartOpen={hubEmptyCartOpen}
                multiCartSwitcherOpen={multiCartSwitcherOpen}
                onOpen={toggleHeaderCart}
                hideWhenEmpty={isRootBrowseHome}
              />
              {isRootBrowseHome ? null : (
                <CustomerSettingsButton open={settingsOpen} onOpen={toggleSettings} />
              )}
              {isRootBrowseHome ? null : (
                <CustomerProfileButton
                  profileOpen={profileSheetOpen}
                  onOpenProfile={toggleProfile}
                />
              )}
            </div>
          </div>
        </div>
      </header>
      <div aria-hidden="true" className={CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS} />
      <CustomerSettingsSheet open={settingsOpen} onClose={closeSettings} />
      <CustomerProfileSheet open={profileSheetOpen} onClose={closeProfile} />
      {discoveryLocation !== null ? (
        <CustomerLocationDisclosureSheet
          open={locationDisclosureOpen}
          onClose={closeLocationDisclosure}
          nearbyActive={discoveryLocation.nearbyActive}
          geoPending={discoveryLocation.geoPending}
          geoErrorKey={discoveryLocation.geoErrorKey}
          radiusMeters={discoveryLocation.radiusMeters}
          requestNearby={discoveryLocation.requestNearby}
          applyManualPlace={discoveryLocation.applyManualPlace}
          clearNearbyContext={discoveryLocation.clearNearbyContext}
        />
      ) : null}
      {/* G1 sibling of <header> — same fragment as Profile; not inside END_SLOT / END_ICONS. */}
      <HubEmptyCartSheet
        open={hubEmptyCartOpen}
        onClose={closeHubEmptyCart}
        onBrowseShops={onBrowseShops}
      />
      <MultiCartSwitcherSheet
        open={multiCartSwitcherOpen}
        onClose={closeMultiCartSwitcher}
        rows={multiCartRows}
      />
    </>
  );
}
